import { createClinicCrfPackage } from './package'
import { deepClone, emptyDefinition } from './runtime'
import type { ClinicCrfPackageV1 } from './types'

export interface ClinicCrfTemplate {
  id: string
  title: string
  description: string
  category: 'start' | 'screening' | 'visit' | 'interview' | 'safety'
  package: ClinicCrfPackageV1
}

const empty = createClinicCrfPackage(
  emptyDefinition(),
  { code: 'NEW_FORM', name: 'Nowy formularz kliniczny', version: 1 },
  {
    default_locale: 'pl',
    sections: [{ id: 'general', title: 'Formularz', field_paths: ['assessment.result'] }],
    fields: {
      'assessment.result': {
        help_text: 'Zmień to przykładowe pole lub dodaj kolejne elementy formularza.',
        option_labels: { yes: 'Tak', no: 'Nie' },
      },
    },
  },
)

const screening = createClinicCrfPackage(
  {
    version: 'clinic-crf.v2',
    fields: [
      { path: 'consent.confirmed', label: 'Potwierdzono ważną zgodę', type: 'boolean', required: true, source: 'site' },
      { path: 'demographics.age', label: 'Wiek w dniu screeningu', type: 'integer', required: true, min: 0, max: 120, source: 'site' },
      { path: 'criteria.inclusion_met', label: 'Spełniono wszystkie kryteria włączenia', type: 'boolean', required: true, source: 'site' },
      { path: 'criteria.exclusion_present', label: 'Występuje kryterium wyłączenia', type: 'boolean', required: true, source: 'site' },
      { path: 'criteria.notes', label: 'Uwagi do oceny kryteriów', type: 'text', max_length: 2000, source: 'site', visible_when: { any: [
        { field: 'criteria.inclusion_met', operator: 'eq', value: false },
        { field: 'criteria.exclusion_present', operator: 'eq', value: true },
      ] } },
      { path: 'eligibility.decision', label: 'Decyzja kwalifikacyjna', type: 'choice', required: true, options: ['pending', 'eligible', 'not_eligible'], source: 'site' },
    ],
    rules: [
      {
        id: 'adult-age',
        path: 'demographics.age',
        message: 'Przykładowy formularz wymaga wieku co najmniej 18 lat.',
        when: { field: 'demographics.age', operator: 'present' },
        assert: { field: 'demographics.age', operator: 'gte', value: 18 },
      },
      {
        id: 'eligible-requires-inclusion',
        path: 'eligibility.decision',
        message: 'Decyzja eligible wymaga spełnienia kryteriów włączenia.',
        when: { field: 'eligibility.decision', operator: 'eq', value: 'eligible' },
        assert: { field: 'criteria.inclusion_met', operator: 'eq', value: true },
      },
      {
        id: 'eligible-requires-no-exclusion',
        path: 'eligibility.decision',
        message: 'Decyzja eligible wymaga braku kryteriów wyłączenia.',
        when: { field: 'eligibility.decision', operator: 'eq', value: 'eligible' },
        assert: { field: 'criteria.exclusion_present', operator: 'eq', value: false },
      },
    ],
  },
  { code: 'SCREENING', name: 'Screening i kwalifikacja', version: 1, description: 'Syntetyczny punkt startowy do projektowania formularza kwalifikacyjnego.' },
  {
    default_locale: 'pl',
    sections: [
      { id: 'consent', title: 'Warunki rozpoczęcia', description: 'Przed oceną kryteriów potwierdź warunki wejściowe.', field_paths: ['consent.confirmed'] },
      { id: 'demographics', title: 'Dane do kwalifikacji', field_paths: ['demographics.age'] },
      { id: 'criteria', title: 'Kryteria protokołu', field_paths: ['criteria.inclusion_met', 'criteria.exclusion_present', 'criteria.notes'] },
      { id: 'decision', title: 'Decyzja', field_paths: ['eligibility.decision'] },
    ],
    fields: {
      'consent.confirmed': { help_text: 'Pole demonstracyjne — rzeczywiste potwierdzenie zgody odbywa się w kontrolowanym eCRF.' },
      'demographics.age': { help_text: 'Wartość syntetyczna używana wyłącznie do testu reguł.' },
      'eligibility.decision': { option_labels: { eligible: 'Zakwalifikowany', not_eligible: 'Nie zakwalifikowany', pending: 'Decyzja oczekuje' } },
    },
  },
)

const baseline = createClinicCrfPackage(
  {
    version: 'clinic-crf.v2',
    fields: [
      { path: 'visit.performed_at', label: 'Data i czas pomiaru', type: 'datetime', required: true, source: 'site' },
      { path: 'vitals.systolic', label: 'Ciśnienie skurczowe', type: 'integer', required: true, min: 40, max: 300, unit: 'mm[Hg]', source: 'device', missing_codes: ['not_done', 'unknown'], missing_reason_required: true },
      { path: 'vitals.diastolic', label: 'Ciśnienie rozkurczowe', type: 'integer', required: true, min: 20, max: 200, unit: 'mm[Hg]', source: 'device', missing_codes: ['not_done', 'unknown'], missing_reason_required: true },
      { path: 'vitals.pulse', label: 'Tętno', type: 'integer', min: 20, max: 250, unit: '/min', source: 'device', missing_codes: ['not_done'] },
      { path: 'vitals.temperature', label: 'Temperatura ciała', type: 'number', precision: 1, min: 30, max: 45, unit: 'Cel', source: 'device', missing_codes: ['not_done'] },
      { path: 'vitals.position', label: 'Pozycja podczas pomiaru', type: 'choice', options: ['sitting', 'standing', 'supine'], source: 'site' },
      { path: 'assessment.comment', label: 'Komentarz kliniczny', type: 'text', max_length: 4000, source: 'site' },
    ],
    rules: [{
      id: 'blood-pressure-order',
      path: 'vitals.systolic',
      message: 'Ciśnienie skurczowe powinno być większe od rozkurczowego.',
      when: { all: [
        { field: 'vitals.systolic', operator: 'present' },
        { field: 'vitals.diastolic', operator: 'present' },
      ] },
      assert: { field: 'vitals.systolic', operator: 'gt', compare_field: 'vitals.diastolic' },
    }],
  },
  { code: 'BASELINE_VITALS', name: 'Wizyta bazowa — parametry życiowe', version: 1 },
  {
    default_locale: 'pl',
    sections: [
      { id: 'visit', title: 'Wizyta', field_paths: ['visit.performed_at'] },
      { id: 'vitals', title: 'Parametry życiowe', description: 'Wprowadź syntetyczne wyniki pomiarów i sprawdź reguły.', field_paths: ['vitals.systolic', 'vitals.diastolic', 'vitals.pulse', 'vitals.temperature', 'vitals.position'] },
      { id: 'assessment', title: 'Ocena', field_paths: ['assessment.comment'] },
    ],
    fields: {
      'visit.performed_at': { help_text: 'Reader normalizuje czas testowy do ISO 8601 ze strefą.' },
      'vitals.position': { option_labels: { sitting: 'Siedząca', standing: 'Stojąca', supine: 'Leżąca' } },
      'assessment.comment': { placeholder: 'Nie wpisuj danych ani obserwacji prawdziwego uczestnika.' },
    },
  },
)

const interview = createClinicCrfPackage(
  {
    version: 'clinic-crf.v2',
    fields: [
      { path: 'history.symptoms_present', label: 'Czy występują obecnie objawy?', type: 'boolean', required: true, source: 'subject' },
      { path: 'history.symptoms', label: 'Zgłaszane objawy', type: 'multichoice', options: ['pain', 'fatigue', 'fever', 'dyspnoea', 'other'], source: 'subject', visible_when: { field: 'history.symptoms_present', operator: 'eq', value: true }, required_when: { field: 'history.symptoms_present', operator: 'eq', value: true } },
      { path: 'history.other_symptom', label: 'Inny objaw', type: 'string', max_length: 500, source: 'subject' },
      { path: 'history.onset', label: 'Przybliżona data początku objawów', type: 'partial_date', source: 'subject', missing_codes: ['unknown', 'withheld'] },
      { path: 'history.allergies_present', label: 'Czy występują znane alergie?', type: 'boolean', required: true, source: 'subject' },
      { path: 'history.allergies', label: 'Opis alergii', type: 'text', max_length: 2000, source: 'subject', visible_when: { field: 'history.allergies_present', operator: 'eq', value: true }, required_when: { field: 'history.allergies_present', operator: 'eq', value: true } },
      { path: 'history.medications', label: 'Stosowane leki — opis testowy', type: 'text', max_length: 4000, source: 'subject', missing_codes: ['not_applicable', 'withheld'] },
    ],
    rules: [],
  },
  { code: 'MEDICAL_HISTORY', name: 'Wywiad kliniczny', version: 1, description: 'Przykładowy formularz rozmowy z logiką warunkową.' },
  {
    default_locale: 'pl',
    sections: [
      { id: 'symptoms', title: 'Objawy', field_paths: ['history.symptoms_present', 'history.symptoms', 'history.other_symptom', 'history.onset'] },
      { id: 'history', title: 'Alergie i leczenie', field_paths: ['history.allergies_present', 'history.allergies', 'history.medications'] },
    ],
    fields: {
      'history.symptoms': { help_text: 'Można wskazać więcej niż jedną syntetyczną odpowiedź.', option_labels: { pain: 'Ból', fatigue: 'Zmęczenie', fever: 'Gorączka', dyspnoea: 'Duszność', other: 'Inny objaw' } },
      'history.other_symptom': { help_text: 'Wypełnij tylko wtedy, gdy wybrano opcję „Inny objaw”.', placeholder: 'Syntetyczny opis objawu' },
      'history.allergies': { placeholder: 'Syntetyczny opis alergii' },
      'history.medications': { placeholder: 'Nie wpisuj rzeczywistego schematu leczenia.' },
    },
  },
)

const adverseEvent = createClinicCrfPackage(
  {
    version: 'clinic-crf.v2',
    fields: [
      { path: 'ae.occurred', label: 'Czy wystąpiło zdarzenie niepożądane?', type: 'boolean', required: true, source: 'site' },
      { path: 'ae.term', label: 'Nazwa zdarzenia', type: 'string', max_length: 500, source: 'site', visible_when: { field: 'ae.occurred', operator: 'eq', value: true }, required_when: { field: 'ae.occurred', operator: 'eq', value: true } },
      { path: 'ae.onset', label: 'Data początku', type: 'partial_date', source: 'site', visible_when: { field: 'ae.occurred', operator: 'eq', value: true }, required_when: { field: 'ae.occurred', operator: 'eq', value: true }, missing_codes: ['unknown'] },
      { path: 'ae.severity', label: 'Nasilenie', type: 'choice', options: ['mild', 'moderate', 'severe'], source: 'site', visible_when: { field: 'ae.occurred', operator: 'eq', value: true }, required_when: { field: 'ae.occurred', operator: 'eq', value: true } },
      { path: 'ae.serious', label: 'Czy zdarzenie jest ciężkie (serious)?', type: 'boolean', source: 'site', visible_when: { field: 'ae.occurred', operator: 'eq', value: true }, required_when: { field: 'ae.occurred', operator: 'eq', value: true } },
      { path: 'ae.seriousness_reason', label: 'Kryterium ciężkości', type: 'multichoice', options: ['death', 'life_threatening', 'hospitalisation', 'disability', 'congenital_anomaly', 'other_medically_important'], source: 'site', visible_when: { field: 'ae.serious', operator: 'eq', value: true }, required_when: { field: 'ae.serious', operator: 'eq', value: true } },
      { path: 'ae.outcome', label: 'Wynik zdarzenia', type: 'choice', options: ['recovered', 'recovering', 'not_recovered', 'recovered_with_sequelae', 'fatal', 'unknown'], source: 'site', visible_when: { field: 'ae.occurred', operator: 'eq', value: true } },
    ],
    rules: [],
  },
  { code: 'ADVERSE_EVENT', name: 'Zdarzenie niepożądane — formularz testowy', version: 1 },
  {
    default_locale: 'pl',
    sections: [
      { id: 'occurrence', title: 'Zdarzenie', field_paths: ['ae.occurred', 'ae.term', 'ae.onset', 'ae.severity'] },
      { id: 'seriousness', title: 'Ciężkość i wynik', field_paths: ['ae.serious', 'ae.seriousness_reason', 'ae.outcome'] },
    ],
    fields: {
      'ae.term': { help_text: 'Przykład nie zawiera słownika MedDRA. Użycie terminologii wymaga właściwej licencji i wersji.', placeholder: 'Syntetyczna nazwa zdarzenia' },
      'ae.severity': { option_labels: { mild: 'Łagodne', moderate: 'Umiarkowane', severe: 'Ciężkie nasilenie' } },
      'ae.outcome': { option_labels: { recovered: 'Ustąpiło', recovering: 'Ustępuje', not_recovered: 'Nie ustąpiło', recovered_with_sequelae: 'Ustąpiło z następstwami', fatal: 'Zgon', unknown: 'Nieznany' } },
    },
  },
)

export const CLINIC_CRF_TEMPLATES: ClinicCrfTemplate[] = [
  { id: 'blank', title: 'Nowy formularz', description: 'Minimalny, poprawny pakiet do własnego projektu.', category: 'start', package: empty },
  { id: 'screening', title: 'Screening i kwalifikacja', description: 'Kryteria, decyzja i bezpieczne reguły cross-field.', category: 'screening', package: screening },
  { id: 'baseline-vitals', title: 'Wizyta bazowa', description: 'Parametry życiowe, jednostki, zakresy i jawne braki.', category: 'visit', package: baseline },
  { id: 'medical-history', title: 'Wywiad kliniczny', description: 'Objawy, alergie i leki z logiką warunkową.', category: 'interview', package: interview },
  { id: 'adverse-event', title: 'Zdarzenie niepożądane', description: 'Syntetyczny formularz AE z kryteriami ciężkości.', category: 'safety', package: adverseEvent },
]

export function templateById(id: string): ClinicCrfTemplate | null {
  const template = CLINIC_CRF_TEMPLATES.find((candidate) => candidate.id === id)
  return template ? deepClone(template) : null
}
